import { useState, useEffect } from 'react';
import { Save, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    gym_name: 'G.O.A.T Fitness',
    gym_phone: '098881 56428',
    gym_address: 'Above HDFC Bank, Saili Road, Pathankot'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, { headers });
      setSettings({
        gym_name: response.data.gym_name || 'G.O.A.T Fitness',
        gym_phone: response.data.gym_phone || '098881 56428',
        gym_address: response.data.gym_address || 'Above HDFC Bank, Saili Road, Pathankot'
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers });
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Loading settings...</div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="font-['Barlow_Condensed'] font-extrabold text-3xl lg:text-4xl uppercase tracking-tight text-white">
          Settings
        </h1>
        <p className="text-zinc-400 mt-1">Manage your gym details</p>
      </div>

      <form onSubmit={handleSaveSettings} className="space-y-6">
        {/* Gym Details */}
        <div className="card-glass">
          <h2 className="font-['Barlow_Condensed'] font-bold text-xl uppercase text-white mb-6">
            Gym Details
          </h2>
          <div className="space-y-4">
            <div className="form-group">
              <Label className="form-label">Gym Name</Label>
              <Input
                data-testid="settings-gym-name"
                value={settings.gym_name}
                onChange={(e) => setSettings({ ...settings, gym_name: e.target.value })}
                className="input-dark"
                placeholder="G.O.A.T Fitness"
              />
            </div>
            <div className="form-group">
              <Label className="form-label">Gym Phone</Label>
              <Input
                data-testid="settings-gym-phone"
                value={settings.gym_phone}
                onChange={(e) => setSettings({ ...settings, gym_phone: e.target.value })}
                className="input-dark"
                placeholder="098881 56428"
              />
            </div>
            <div className="form-group">
              <Label className="form-label">Gym Address</Label>
              <Input
                data-testid="settings-gym-address"
                value={settings.gym_address}
                onChange={(e) => setSettings({ ...settings, gym_address: e.target.value })}
                className="input-dark"
                placeholder="Above HDFC Bank, Saili Road, Pathankot"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            data-testid="save-settings-btn"
            type="submit"
            disabled={saving}
            className="bg-[#FF3B30] hover:bg-white hover:text-black text-white font-bold uppercase tracking-wider rounded-none px-8"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>

      {/* Credits */}
      <div className="card-glass border-zinc-700">
        <p className="text-zinc-500 text-sm text-center">
          Designed & Developed by{' '}
          <a 
            href="https://www.wizbang.in" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#FF3B30] hover:text-white transition-colors"
          >
            Wizbang India
          </a>
        </p>
      </div>
    </div>
  );
};

export default SettingsPage;
